'use client';

import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from '@repo/shadcn';
import { useState } from 'react';

('use client');

interface PaginationComponentProps {
    totalItems: number;
    itemsPerPage: number;
    initialPage?: number;
    onPageChange?: (page: number) => void;
    showEllipsis?: boolean;
    maxVisiblePages?: number;
    showTotalItems?: boolean;
}

export default function PaginationComponent({
    totalItems,
    itemsPerPage,
    initialPage = 1,
    onPageChange,
    showEllipsis = true,
    maxVisiblePages = 5,
    showTotalItems: _ = false,
}: PaginationComponentProps) {
    const [currentPage, setCurrentPage] = useState(initialPage);
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const handlePageChange = (page: number) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
        onPageChange?.(page);
    };

    // Calculate which page numbers to show
    const getVisiblePages = () => {
        if (totalPages <= maxVisiblePages) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const halfVisible = Math.floor(maxVisiblePages / 2);
        let startPage = Math.max(currentPage - halfVisible, 1);
        const endPage = Math.min(startPage + maxVisiblePages - 1, totalPages);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(endPage - maxVisiblePages + 1, 1);
        }

        return Array.from(
            { length: endPage - startPage + 1 },
            (_, i) => startPage + i,
        );
    };

    const visiblePages = getVisiblePages();
    const showStartEllipsis = showEllipsis && visiblePages[0] > 1;
    const showEndEllipsis =
        showEllipsis && visiblePages[visiblePages.length - 1] < totalPages;

    const renderPagination = () => (
        <Pagination className="my-4">
            <PaginationContent>
                <PaginationItem>
                    <PaginationPrevious
                        href="#"
                        onClick={(e: any) => {
                            e.preventDefault();
                            handlePageChange(currentPage - 1);
                        }}
                        aria-disabled={currentPage === 1}
                        className={
                            currentPage === 1
                                ? 'pointer-events-none opacity-50'
                                : ''
                        }
                        aria-label="Go to previous page"
                    />
                </PaginationItem>

                {showStartEllipsis && (
                    <>
                        <PaginationItem>
                            <PaginationLink
                                href="#"
                                onClick={(e: any) => {
                                    e.preventDefault();
                                    handlePageChange(1);
                                }}
                                aria-label="Go to page 1"
                            >
                                1
                            </PaginationLink>
                        </PaginationItem>
                        <PaginationItem>
                            <PaginationEllipsis />
                        </PaginationItem>
                    </>
                )}

                {visiblePages.map((page) => (
                    <PaginationItem key={page}>
                        <PaginationLink
                            href="#"
                            onClick={(e: any) => {
                                e.preventDefault();
                                handlePageChange(page);
                            }}
                            isActive={page === currentPage}
                            aria-current={
                                page === currentPage ? 'page' : undefined
                            }
                            aria-label={`Go to page ${page}`}
                        >
                            {page}
                        </PaginationLink>
                    </PaginationItem>
                ))}

                {showEndEllipsis && (
                    <>
                        <PaginationItem>
                            <PaginationEllipsis />
                        </PaginationItem>
                        <PaginationItem>
                            <PaginationLink
                                href="#"
                                onClick={(e: any) => {
                                    e.preventDefault();
                                    handlePageChange(totalPages);
                                }}
                                aria-label={`Go to page ${totalPages}`}
                            >
                                {totalPages}
                            </PaginationLink>
                        </PaginationItem>
                    </>
                )}

                <PaginationItem>
                    <PaginationNext
                        href="#"
                        onClick={(e: any) => {
                            e.preventDefault();
                            handlePageChange(currentPage + 1);
                        }}
                        aria-disabled={currentPage === totalPages}
                        className={
                            currentPage === totalPages
                                ? 'pointer-events-none opacity-50'
                                : ''
                        }
                        aria-label="Go to next page"
                    />
                </PaginationItem>
            </PaginationContent>
        </Pagination>
    );

    // if (showTotalItems) {
    //     return (
    //         <div className="flex items-center justify-between">
    //             <span className="flex grow items-center text-sm">
    //                 <span>Total Items: </span>
    //                 <span>{totalItems}</span>
    //             </span>
    //             {renderPagination()}
    //         </div>
    //     );
    // }

    return renderPagination();
}
